import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useNavCounts } from "@/contexts/NavCountsContext";
import {
  SignOut, Heartbeat, ChartBar, Gear, Plant, Trophy,
  CalendarHeart, Sparkle, PhoneCall, Phone, Crown, Buildings,
  ClipboardText, Bell, ArrowSquareOut, ChartLineUp,
} from "@phosphor-icons/react";
import { Toaster } from "@/components/ui/sonner";
import useReminderAlerts from "@/lib/useReminderAlerts";

const SIDEBAR_WIDTH = 232;

// Purple filled badge for sidebar nav items. Renders nothing when count is 0.
function NavBadge({ count, testId }) {
  if (!count) return null;
  const label = count > 99 ? "99+" : String(count);
  return (
    <span
      className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-violet-50 border border-violet-200 text-violet-700 text-[10px] font-semibold leading-none tabular-nums"
      data-testid={testId}
    >
      {label}
    </span>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdminPlus = user?.role === "admin" || user?.role === "super_admin";

  // Poll for due reminder alerts (browser desktop notifications).
  useReminderAlerts();

  // Sidebar badge counts come from the shared NavCountsContext so any page
  // that mutates a task/reminder can push the counts to refresh instantly.
  const { taskCount, reminderCount } = useNavCounts();

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive
        ? "bg-gray-900 text-white"
        : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
    }`;

  // Multi-POC support: prefer the new poc_names array; fall back to legacy poc_name.
  const pocList = (user?.poc_names && user.poc_names.length > 0)
    ? user.poc_names
    : (user?.poc_name ? [user.poc_name] : []);
  const roleLabel =
    user?.role === "poc" && pocList.length > 0
      ? `POC · ${pocList.join(", ")}`
      : user?.role === "super_admin"
      ? "Super Admin"
      : user?.role;

  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Sidebar */}
      <aside
        className="fixed top-0 left-0 h-screen bg-white border-r border-gray-200 flex flex-col z-30"
        style={{ width: SIDEBAR_WIDTH }}
        data-testid="app-sidebar"
      >
        {/* Brand */}
        <Link
          to="/dashboard"
          className="flex items-center gap-2.5 px-4 h-14 border-b border-gray-200 shrink-0"
          data-testid="brand-link"
        >
          <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center">
            <Heartbeat size={18} weight="fill" className="text-red-500 animate-brand-pulse" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-[15px] font-semibold text-gray-900">
              Pulse
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 -mt-0.5">
              AssureAI
            </div>
          </div>
        </Link>

        {/* Scrollable nav */}
        <nav
          className="flex-1 overflow-y-auto px-2 py-3 space-y-1"
          data-testid="main-nav"
        >
          {/* Customers group */}
          <div className="px-2 pt-1 pb-1 text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
            Customers
          </div>
          <NavLink to="/dashboard" className={linkClass} data-testid="nav-dashboard">
            <ChartBar size={16} />
            Dashboard
          </NavLink>
          <NavLink to="/new-customers" className={linkClass} data-testid="nav-new-customers">
            <Plant size={16} />
            New Customers
          </NavLink>
          <NavLink to="/old-customers" className={linkClass} data-testid="nav-old-customers">
            <Buildings size={16} />
            Old Customers
          </NavLink>
          <NavLink to="/enterprise" className={linkClass} data-testid="nav-enterprise">
            <Crown size={16} />
            Enterprise
          </NavLink>
          <NavLink to="/renewals" className={linkClass} data-testid="nav-renewals">
            <CalendarHeart size={16} />
            Renewal Customers
          </NavLink>
          <NavLink to="/power-users" className={linkClass} data-testid="nav-power-users">
            <Trophy size={16} />
            Power Users
          </NavLink>
          <NavLink to="/usage-metrics" className={linkClass} data-testid="nav-usage-metrics">
            <ChartLineUp size={16} />
            Usage Metrics
          </NavLink>
          <NavLink to="/follow-ups" className={linkClass} data-testid="nav-follow-ups">
            <Phone size={16} />
            Follow-ups
          </NavLink>
          <NavLink to="/activity" className={linkClass} data-testid="nav-activity">
            <PhoneCall size={16} />
            Activity
          </NavLink>
          {isAdminPlus && (
            <NavLink to="/mis" className={linkClass} data-testid="nav-mis">
              <Sparkle size={16} />
              MIS
            </NavLink>
          )}

          {/* Workflow group */}
          <div className="px-2 pt-3 pb-1 text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
            Workflow
          </div>
          <NavLink to="/tasks" className={linkClass} data-testid="nav-tasks">
            <ClipboardText size={16} />
            <span className="flex-1">Pending Tasks</span>
            <NavBadge count={taskCount} testId="nav-tasks-count" />
          </NavLink>
          <NavLink to="/reminders" className={linkClass} data-testid="nav-reminders">
            <Bell size={16} />
            <span className="flex-1">Reminders</span>
            <NavBadge count={reminderCount} testId="nav-reminders-count" />
          </NavLink>

          {/* Switch App group */}
          <div className="px-2 pt-3 pb-1 text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
            Switch App
          </div>
          <a
            href="https://vitals.assuredev.in"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 transition-colors"
            data-testid="nav-switch-vitals"
          >
            <ArrowSquareOut size={16} weight="bold" />
            <span className="flex-1">Switch to Vitals</span>
          </a>
        </nav>

        {/* Pinned bottom: Admin + user card + sign out */}
        <div className="shrink-0 border-t border-gray-200 bg-white" data-testid="sidebar-bottom">
          {isAdminPlus && (
            <div className="px-2 pt-2">
              <NavLink to="/admin" className={linkClass} data-testid="nav-admin">
                <Gear size={16} />
                Admin
              </NavLink>
            </div>
          )}

          <div className="px-3 py-3 mt-1 border-t border-gray-100 flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-violet-700 text-white flex items-center justify-center text-[11px] font-semibold shrink-0">
              {initials}
            </div>
            <div className="leading-tight min-w-0 flex-1">
              <div
                className="text-[13px] font-medium text-gray-900 truncate"
                data-testid="user-name"
                title={user?.name}
              >
                {user?.name}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 truncate">
                {roleLabel}
              </div>
            </div>
            <button
              onClick={onLogout}
              className="p-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              data-testid="logout-btn"
              title="Sign out"
            >
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main
        className="min-h-screen px-6 py-8 animate-fade-up"
        style={{ marginLeft: SIDEBAR_WIDTH }}
      >
        <div className="max-w-[1240px] mx-auto">{children}</div>
      </main>
      <Toaster position="top-right" />
    </div>
  );
}
